import React, { FC, useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import Activation from '../Index';
import { initialValues, validationSchema } from './form';
import {
  IActivationEducation,
  IEducationFormState
} from 'core/interface/activation';
import { days, months, years } from 'core/constants/aboutMePopUpConstants';
import { IAuthSteps } from 'core/interface/authSteps';
import { ActivationPageEnum } from 'core/enum/activationPage';
import DropDown from 'components/Dropdown';
import Checkboxes from 'components/shared/Checkbox/Index';
import Button from 'components/Button/Index';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';
import { ProfileService } from 'services/profile/profile';
import Autocomplete from 'components/shared/Autocomplete/Index';
import { getDateFormatter } from 'helpers/dateConverter';
import { EducationService } from 'services/education/educationService';
import { IMe } from 'core/interface/me';
import { useSelector } from 'react-redux';
import { Degrees } from 'core/constants/degreeConstants';
import { IDegreeRedux, ISchoolRedux } from 'core/interface/education';
import { RootState, store } from '../../../../redux/store';
import {
  RemoveEducationForm,
  SaveEducationForm
} from '../../../../redux/slices/educationForm';
import { IEducationSideItem } from 'core/interface/education-side';
import { useActivationTestMode } from 'core/context/ActivationTestMode';

import './Index.scss';

const Education: FC<IAuthSteps> = ({
  t,
  onClick,
  closePopup,
  showTopSide,
  className,
  educationClassName,
  educationInitialValues,
  isOneBtn = false,
  isPopup = false
}): JSX.Element => {
  const schools =
    useSelector((state: ISchoolRedux) => state.schools).schools || [];
  const [loader, setLoader] = useState<boolean>(false);
  const study =
    useSelector((state: IDegreeRedux) => state.study).degrees || [];
  const [dateError, setDateError] = useState<string>('');

  const [formValues, setFormValues] =
    useState<IActivationEducation>(initialValues);
  const me = useSelector((state: IMe) => state.user).user;
  const educationForm = useSelector(
    (state: IEducationFormState) => state.educationForm
  ).educationForm;

  const { educations } = useSelector((state: RootState) => state.educations);
  const {
    enabled: isTestMode,
    addEducation,
    educations: testEducations,
    recordRequest
  } = useActivationTestMode();
  const listedEducations = isTestMode ? testEducations : educations;

  useEffect(() => {
    if (educationForm) {
      setFormValues(educationForm);
    }
  }, [educationForm]);

  useEffect(() => {
    getSchools();
    getDegrees();
    if (!isTestMode) {
      getEducations();
    }
  }, [me]);

  useEffect(() => {
    if (educationInitialValues) {
      setFormValues(educationInitialValues);
    }
  }, [educationInitialValues]);

  const getEducations = (): void => {
    EducationService.getEducations(me.candidate_id);
  };

  const getSchools = (schools: string = '') => {
    ProfileService.getSchools(schools);
  };

  const getDegrees = (deg: string = '') => {
    ProfileService.getDegrees(deg);
  };

  const saveValuesInState = (values: IActivationEducation) => {
    const { dispatch } = store;
    dispatch(SaveEducationForm(values));
  };

  const handleSubmit = async (
    values: IActivationEducation,
    actions?: FormikHelpers<IActivationEducation>
  ): Promise<void> => {
    // Check if there's a date validation error only when all finish date fields are provided
    if (
      values.start.year &&
      values.start.month &&
      values.start.day &&
      values.finish.year &&
      values.finish.month &&
      values.finish.day
    ) {
      const startDate = new Date(
        `${values.start.year}-${values.start.month}-${values.start.day}`
      );
      const finishDate = new Date(
        `${values.finish.year}-${values.finish.month}-${values.finish.day}`
      );

      if (finishDate < startDate) {
        setDateError('Finish date must be after or equal to start date');
        return; // Stop submission if dates are invalid
      }
    }

    setLoader(true);

    // Determine if end date should be null
    const isEndDateNull =
      !values.finish.year || !values.finish.month || !values.finish.day;

    const body = {
      school:
        schools.find((sch) => sch.name === values.school)?.id || values.school,
      study:
        study.find((studies) => studies.name === values.study_field)?.id ||
        values.study_field,
      degree: values.degree,
      start: `${values.start.year}-${values.start.month}-${values.start.day}`,
      end: !isEndDateNull
        ? `${values.finish.year}-${values.finish.month}-${values.finish.day}`
        : null,
      graduate: values.isGraduated
    } as any;

    if (isTestMode) {
      const educationId = Date.now();
      addEducation({
        id: educationId,
        school: { name: values.school },
        study: { name: values.study_field },
        degree: values.degree || '',
        start: body.start,
        end: body.end,
        graduate: values.isGraduated
      });
      recordRequest({
        id: `education-${educationId}`,
        method: 'POST',
        url: `/candidates/${me.candidate_id}/educations/`,
        description: 'Create education',
        body
      });
      RemoveEducationForm();
      actions?.resetForm();
      onClick(ActivationPageEnum.EDUCATION_VIEW);
      closePopup && closePopup();
      setLoader(false);
      return;
    }

    if (educationInitialValues && educationInitialValues.id) {
      await ProfileService.editEducation(
        me.candidate_id,
        educationInitialValues.id,
        body
      );
    } else {
      await EducationService.setEducation(me.candidate_id, body);
    }
    RemoveEducationForm();
    actions?.resetForm();
    onClick(ActivationPageEnum.EDUCATION_VIEW);
    closePopup && closePopup();
    setLoader(false);
  };

  const labelStyle = {
    fontSize: '16px !important',
    color: '#000',
    fontWeight: '400',
    display: 'block',
    marginBottom: '8px'
  };

  return (
    <Activation
      showTopSide={showTopSide}
      className={
        isPopup ? '' : `activation-edu ${className ?? ''} ${className}`
      }
      title={t('What is your education')}
      currentStep={3}
      isPopup={true}
    >
      <div className={`edu ${educationClassName ?? ''}`}>
        <Formik
          enableReinitialize
          initialValues={formValues}
          onSubmit={(values, actions) => handleSubmit(values, actions)}
          validationSchema={validationSchema()}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({
            setFieldValue,
            values,
            isValid,
            errors,
            touched,
            setErrors,
            setFieldTouched
          }) => {
            // Check for date validation errors after form values change
            useEffect(() => {
              // Only validate dates if all finish date fields are filled
              // This allows finish date to be optional
              if (
                values.start.year &&
                values.start.month &&
                values.start.day &&
                values.finish.year &&
                values.finish.month &&
                values.finish.day
              ) {
                const startDate = new Date(
                  `${values.start.year}-${values.start.month}-${values.start.day}`
                );
                const finishDate = new Date(
                  `${values.finish.year}-${values.finish.month}-${values.finish.day}`
                );

                if (finishDate < startDate) {
                  setDateError(
                    'Finish date must be after or equal to start date'
                  );
                } else {
                  setDateError('');
                }
              } else {
                // No error if finish date is not completely filled
                setDateError('');
              }
            }, [values.start, values.finish]);

            return (
              <Form>
                <div className="edu__top">
                  <div className="field edu-fields">
                    <span style={labelStyle}>School</span>
                    <Autocomplete
                      fieldName="school"
                      value={values.school}
                      className="inputs"
                      options={
                        schools.map((item: IAutocompleteItem) => ({
                          id: item.id,
                          title: item.name
                        })) || []
                      }
                      placeholder="Schools"
                      show_location={false}
                      onInputChange={(e) => {
                        setFieldValue('school', e);
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        // Allow any input value for school
                        const val = e.target.value;
                        setFieldValue('school', val);
                      }}
                      values={schools}
                    />
                  </div>
                  <div className="field">
                    <span style={labelStyle}>{t('Field of study')}</span>
                    <Autocomplete
                      fieldName="study_field"
                      value={values.study_field}
                      className="inputs"
                      options={
                        study.map((item: IAutocompleteItem) => ({
                          id: item.id,
                          title: item.name
                        })) || []
                      }
                      placeholder={t('Field of study')}
                      show_location={false}
                      onInputChange={(e) => {
                        setFieldValue('study_field', e);
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        // Allow any input value for study field
                        const val = e.target.value;
                        setFieldValue('study_field', val);
                      }}
                      values={study}
                    />
                  </div>
                </div>
                <div className="field">
                  <span style={labelStyle}>{t('Degrees')}</span>
                  <DropDown
                    defaultValue={values.degree}
                    error={touched.degree && errors.degree ? errors.degree : ''}
                    placeholder="Degrees"
                    dropdownClassName="dropdown"
                    items={Degrees}
                    selectedValue={(value) => {
                      setFieldTouched('degree', true);
                      setFieldValue('degree', value);
                    }}
                  />
                </div>
                <div className="edu__top date">
                  <div className="edu__top-left left-edu">
                    <div className="field start-field">
                      <span style={labelStyle}>{t('Start')}</span>
                      <DropDown
                        error={
                          touched.start?.month && touched.start?.month
                            ? errors.start?.month
                            : ''
                        }
                        placeholder="Month"
                        dropdownClassName="dropdown month"
                        defaultValue={values.start.month}
                        items={months}
                        selectedValue={(value) => {
                          setFieldTouched('start.month', true);
                          setFieldValue('start.month', value);
                        }}
                      />
                    </div>
                    <div className="field start-field">
                      <span style={labelStyle}>&nbsp;</span>
                      <DropDown
                        error={
                          touched.start?.day && touched.start?.day
                            ? errors.start?.day
                            : ''
                        }
                        placeholder="Day"
                        defaultValue={(values.start && values.start.day) || ''}
                        dropdownClassName="dropdown day"
                        items={days}
                        selectedValue={(value) => {
                          setFieldTouched('start.day', true);
                          setFieldValue('start.day', value);
                        }}
                      />
                    </div>
                    <div className="field year-field">
                      <span style={labelStyle}>&nbsp;</span>
                      <DropDown
                        error={
                          touched.start?.year && touched.start?.year
                            ? errors.start?.year
                            : ''
                        }
                        placeholder="Year"
                        dropdownClassName="dropdown"
                        defaultValue={values.start.year}
                        items={years}
                        selectedValue={(value) => {
                          setFieldTouched('start.year', true);
                          setFieldValue('start.year', value);
                        }}
                      />
                    </div>
                  </div>
                  <div className="edu__top-left left-edu no-margin">
                    <div className="field start-field">
                      <span style={labelStyle}>{t('Finish')}</span>
                      <DropDown
                        error={
                          touched.finish?.month && touched.finish?.month
                            ? errors.finish?.month
                            : ''
                        }
                        placeholder="Month"
                        dropdownClassName="dropdown month"
                        defaultValue={values.finish.month}
                        items={months}
                        selectedValue={(value) => {
                          setFieldTouched('finish.month', true);
                          setFieldValue('finish.month', value);
                        }}
                      />
                    </div>
                    <div className="field start-field  day-field">
                      <span style={labelStyle}>&nbsp;</span>
                      <DropDown
                        error={
                          touched.finish?.day && touched.finish?.day
                            ? errors.finish?.day
                            : ''
                        }
                        placeholder="Day"
                        defaultValue={values.finish.day}
                        dropdownClassName="dropdown day"
                        items={days}
                        selectedValue={(value) => {
                          setFieldTouched('finish.day', true);
                          setFieldValue('finish.day', value);
                        }}
                      />
                    </div>
                    <div className="field year-drop">
                      <span style={labelStyle}>&nbsp;</span>
                      <DropDown
                        error={
                          touched.finish?.year && touched.finish?.year
                            ? errors.finish?.year
                            : ''
                        }
                        placeholder="Year"
                        dropdownClassName="dropdown"
                        defaultValue={
                          values.finish.year || formValues.finish.year
                        }
                        items={years}
                        maxRange={+values.start.year}
                        selectedValue={(value) => {
                          setFieldValue('finish.year', value);
                          setFieldTouched('finish.year', true);
                          setFieldValue('finish.year', value);
                          setFormValues(() => ({
                            ...values,
                            finish: { ...values.finish, year: value }
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
                {/* Display date validation error */}
                {dateError && (
                  <div
                    className="error-message"
                    style={{
                      width: '100%',
                      display: 'block',
                      textAlign: 'left',
                      color: 'red',
                      marginTop: '12px'
                    }}
                  >
                    {dateError}
                  </div>
                )}
                <div className="field" style={{ marginTop: '12px' }}>
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('isGraduated', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: 'I graduated from this study',
                        isChecked: values.isGraduated
                      }
                    ]}
                  />
                </div>
                <div className="buttons">
                  {!isOneBtn && (
                    <Button
                      className="back button"
                      t={t}
                      title="back"
                      onClick={() => {
                        saveValuesInState(values);
                        onClick(ActivationPageEnum.MATCH_PREFERENCES);
                      }}
                    />
                  )}
                  <div className="nex-btn-s">
                    {!isPopup && listedEducations && listedEducations.length > 0 && (
                      <Button
                        isLoading={loader}
                        className="next button skip"
                        t={t}
                        onClick={() =>
                          onClick(ActivationPageEnum.EDUCATION_VIEW)
                        }
                        title={'Skip'}
                        disableLoading
                      />
                    )}

                    <Button
                      isLoading={loader}
                      className={`next button ${
                        !isValid || dateError ? 'invalid-btn' : ''
                      } ${isOneBtn ? 'one-btn' : ''}`}
                      type="submit"
                      t={t}
                      title={isOneBtn ? 'Save' : 'Next'}
                      disabled={!!dateError}
                    />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Activation>
  );
};

export default Education;
